"use client";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { PiDownloadSimple, PiLockBold, PiVideo } from "react-icons/pi";

import PaywallTracker from "@/components/analytics/paywall-tracker";
import { Button } from "@/components/button/button";
import Input from "@/components/input/input";
import { PAGES } from "@/configs/pages.config";
import type { MediaAvailability } from "@/funcs/fetch/file.fetch";
import {
  classifyPreviewFailureReason,
  getSourceFromUrl,
  trackAnalyticsApiEvent,
  trackLinkPasted,
  trackPremiumFeatureClicked,
} from "@/lib/analytics";
import { buildEndpointCandidates, fetchWithFallback } from "@/lib/fetch-with-fallback";
import { DEFAULT_LOCALE, getLocaleFromPathname, SupportedLocale } from "@/lib/i18n/locales";
import { mediaRetentionCopy } from "@/lib/i18n/media-retention";
import { withSeoLocalePath } from "@/lib/i18n/seo-paths";
import { getProxiedImageSrc } from "@/lib/image-proxy";
import { isValidSocialUrl, normalizeExternalUrl } from "@/lib/social-url";
import { buildPathWithTrialLinkClaim } from "@/lib/trial-link-claim";

type InstagramPreviewMode = "transcript" | "text" | "summary" | "recipe";

type InstagramRecipe = {
  title?: string;
  description?: string;
  servings?: string;
  prep_time_minutes?: number;
  cook_time_minutes?: number;
  total_time_minutes?: number;
  ingredients?: string[];
  steps?: string[];
  notes?: string[];
};

type InstagramHookPreview = {
  hook?: string;
};

type InstagramBlogPreviewSection = string | { heading?: string; content?: string };

type InstagramBlogPreview = {
  title?: string;
  intro?: string;
  sections?: InstagramBlogPreviewSection[];
  conclusion?: string;
};

type InstagramPreviewResponse =
  | {
      status: "ok";
      media?: MediaAvailability | null;
      trial_claim_token?: string;
      video: {
        id: string;
        title?: string;
        caption?: string;
        duration_sec: number;
        thumbnail_url: string;
        author: {
          username: string;
          display_name: string;
        };
        view_count?: number;
        like_count?: number;
      };
      preview: {
        teaser_summary?: string;
        teaser_transcript?: string;
      };
      recipe?: InstagramRecipe;
      hook?: InstagramHookPreview;
      blog?: InstagramBlogPreview;
    }
  | {
      status: "error";
      message: string;
      reason?: string;
    };

export type InstagramPreviewSuccess = Extract<InstagramPreviewResponse, { status: "ok" }>;

type InstagramReelIntakeProps = {
  mode: InstagramPreviewMode;
  variant?: "section" | "hero";
  previewEndpoint?: string;
  platform?: "instagram" | "tiktok";
  copyOverride?: Partial<
    Pick<
      LocaleCopy,
      "title" | "subtitle" | "analyzeButton" | "analyzingButton" | "placeholder" | "lockedHint" | "lockedDescription" | "cta"
    >
  >;
  onPreviewLoaded?: (response: InstagramPreviewSuccess | null) => void;
  externalPreview?: InstagramPreviewSuccess | null;
  hideInput?: boolean;
  thumbnailScale?: number;
};

type LocaleCopy = {
  title: string;
  subtitle: string;
  analyzeButton: string;
  analyzingButton: string;
  analyzeButtonTikTok: string;
  analyzingButtonTikTok: string;
  rateLimitButton: string;
  placeholder: string;
  summaryPreviewLabel: string;
  transcriptPreviewLabel: string;
  thumbnailAlt: string;
  viewsLabel: string;
  likesLabel: string;
  lockedHint: string;
  lockedDescription: string;
  cta: string;
  errorInvalidUrl: string;
  errorMissingApi: string;
  errorRateLimit: string;
  errorNetwork: string;
  loadingHint: string;
  loadingMessages: string[];
  downloadCtaTitle: string;
  downloadCtaDescription: string;
  downloadVideoButton: string;
  downloadThumbnailButton: string;
};

const localeCopy: Record<SupportedLocale, LocaleCopy> = {
  en: {
    title: "Paste your Instagram Reel link",
    subtitle: "We'll fetch the Reel details and show you a preview.",
    analyzeButton: "Analyze Reel",
    analyzingButton: "Analyzing...",
    analyzeButtonTikTok: "Analyze TikTok",
    analyzingButtonTikTok: "Analyzing TikTok...",
    rateLimitButton: "Sign up to continue",
    placeholder: "https://www.instagram.com/reel/...",
    summaryPreviewLabel: "Summary preview",
    transcriptPreviewLabel: "Transcript preview",
    thumbnailAlt: "Instagram Reel thumbnail",
    viewsLabel: "views",
    likesLabel: "likes",
    lockedHint: "Continue to unlock the full output.",
    lockedDescription: "Get the complete result and export in multiple formats.",
    cta: "Get full transcript",
    errorInvalidUrl: "Please enter a valid Instagram Reel link.",
    errorMissingApi: "Missing API endpoint configuration. Please try again later.",
    errorRateLimit: "Rate limit exceeded. Sign up to continue.",
    errorNetwork: "We couldn't reach Instagram. Please try again.",
    loadingHint: "Most previews are ready in about 30 seconds.",
    loadingMessages: [
      "Our dolphin is catching the video.",
      "Our dolphin is transcribing the audio.",
      "Our dolphin is cooking the preview.",
      "Our dolphin is polishing the final lines.",
      "This one might be a banger.",
    ],
    downloadCtaTitle: "Unlock downloads",
    downloadCtaDescription: "Sign up free to download the original video and thumbnail.",
    downloadVideoButton: "Download video",
    downloadThumbnailButton: "Download thumbnail",
  },
  es: {
    title: "Pega el enlace del Reel de Instagram",
    subtitle: "Obtendremos los detalles del Reel y mostraremos una vista previa.",
    analyzeButton: "Analizar Reel",
    analyzingButton: "Analizando...",
    analyzeButtonTikTok: "Analizar TikTok",
    analyzingButtonTikTok: "Analizando TikTok...",
    rateLimitButton: "Regístrate para continuar",
    placeholder: "https://www.instagram.com/reel/...",
    summaryPreviewLabel: "Vista previa del resumen",
    transcriptPreviewLabel: "Vista previa de la transcripción",
    thumbnailAlt: "Miniatura del Reel de Instagram",
    viewsLabel: "Vistas",
    likesLabel: "Me gusta",
    lockedHint: "Continúa para desbloquear el resultado completo.",
    lockedDescription: "Obtén el resultado completo y exporta en varios formatos.",
    cta: "Obtener transcripción completa",
    errorInvalidUrl: "Introduce un enlace válido de Reel de Instagram.",
    errorMissingApi: "Falta la configuración del endpoint de API. Inténtalo de nuevo más tarde.",
    errorRateLimit: "Se superó el límite de solicitudes. Regístrate para seguir usando la función de Reels de Instagram.",
    errorNetwork: "No pudimos conectar con Instagram. Inténtalo de nuevo.",
    loadingHint: "La mayoría de las vistas previas están listas en unos 30 segundos.",
    loadingMessages: [
      "Nuestro delfín está atrapando el video.",
      "Nuestro delfín está transcribiendo el audio.",
      "Nuestro delfín está cocinando la vista previa.",
      "Nuestro delfín está puliendo las líneas finales.",
      "Este podría ser un bombazo.",
    ],
    downloadCtaTitle: "Desbloquea las descargas",
    downloadCtaDescription: "Regístrate gratis para descargar el video original y la miniatura.",
    downloadVideoButton: "Descargar video",
    downloadThumbnailButton: "Descargar miniatura",
  },
  fr: {
    title: "Collez le lien d’un Reel Instagram",
    subtitle: "Nous récupérerons les détails du Reel et afficherons un aperçu.",
    analyzeButton: "Analyser le Reel",
    analyzingButton: "Analyse en cours...",
    analyzeButtonTikTok: "Analyser TikTok",
    analyzingButtonTikTok: "Analyse de TikTok...",
    rateLimitButton: "Inscrivez-vous pour continuer",
    placeholder: "https://www.instagram.com/reel/...",
    summaryPreviewLabel: "Aperçu du résumé",
    transcriptPreviewLabel: "Aperçu de la transcription",
    thumbnailAlt: "Miniature du Reel Instagram",
    viewsLabel: "Vues",
    likesLabel: "J'aime",
    lockedHint: "Continuez pour débloquer le résultat complet.",
    lockedDescription: "Obtenez le résultat complet et exportez en plusieurs formats.",
    cta: "Obtenir la transcription complète",
    errorInvalidUrl: "Veuillez saisir un lien Reel Instagram valide.",
    errorMissingApi: "Configuration de l'endpoint API manquante. Veuillez réessayer plus tard.",
    errorRateLimit: "Limite dépassée. Inscrivez-vous pour continuer à utiliser la fonctionnalité Reels Instagram.",
    errorNetwork: "Impossible de joindre Instagram. Veuillez réessayer.",
    loadingHint: "La plupart des aperçus sont prêts en environ 30 secondes.",
    loadingMessages: [
      "Notre dauphin attrape la vidéo.",
      "Notre dauphin transcrit l'audio.",
      "Notre dauphin prépare l'aperçu.",
      "Notre dauphin peaufine les dernières lignes.",
      "Celui-ci pourrait être un carton.",
    ],
    downloadCtaTitle: "Débloquez les téléchargements",
    downloadCtaDescription: "Inscrivez-vous gratuitement pour télécharger la vidéo originale et la miniature.",
    downloadVideoButton: "Télécharger la vidéo",
    downloadThumbnailButton: "Télécharger la miniature",
  },
  de: {
    title: "Füge den Instagram-Reel-Link ein",
    subtitle: "Wir rufen die Reel-Details ab und zeigen eine Vorschau.",
    analyzeButton: "Reel analysieren",
    analyzingButton: "Analyse läuft...",
    analyzeButtonTikTok: "TikTok analysieren",
    analyzingButtonTikTok: "TikTok wird analysiert...",
    rateLimitButton: "Registriere dich, um fortzufahren",
    placeholder: "https://www.instagram.com/reel/...",
    summaryPreviewLabel: "Zusammenfassungsvorschau",
    transcriptPreviewLabel: "Transkriptvorschau",
    thumbnailAlt: "Instagram-Reel-Vorschaubild",
    viewsLabel: "Aufrufe",
    likesLabel: "Likes",
    lockedHint: "Weiter, um die vollständige Ausgabe freizuschalten.",
    lockedDescription: "Erhalte das vollständige Ergebnis und exportiere in mehreren Formaten.",
    cta: "Vollständiges Transkript erhalten",
    errorInvalidUrl: "Bitte gib einen gueltigen Instagram-Reel-Link ein.",
    errorMissingApi: "API-Endpunkt-Konfiguration fehlt. Bitte versuche es später erneut.",
    errorRateLimit: "Limit erreicht. Registriere dich, um die Instagram-Reel-Funktion weiter zu nutzen.",
    errorNetwork: "Instagram konnte nicht erreicht werden. Bitte versuche es erneut.",
    loadingHint: "Die meisten Vorschauen sind in etwa 30 Sekunden fertig.",
    loadingMessages: [
      "Unser Delfin schnappt sich das Video.",
      "Unser Delfin transkribiert den Ton.",
      "Unser Delfin bereitet die Vorschau zu.",
      "Unser Delfin feilt an den letzten Zeilen.",
      "Das hier könnte ein Banger sein.",
    ],
    downloadCtaTitle: "Downloads freischalten",
    downloadCtaDescription: "Registriere dich kostenlos, um das Originalvideo und das Vorschaubild herunterzuladen.",
    downloadVideoButton: "Video herunterladen",
    downloadThumbnailButton: "Thumbnail herunterladen",
  },
  it: {
    title: "Incolla il link del Reel di Instagram",
    subtitle: "Recupereremo i dettagli del Reel e mostreremo un'anteprima.",
    analyzeButton: "Analizza il Reel",
    analyzingButton: "Analisi in corso...",
    analyzeButtonTikTok: "Analizza TikTok",
    analyzingButtonTikTok: "Analisi di TikTok...",
    rateLimitButton: "Registrati per continuare",
    placeholder: "https://www.instagram.com/reel/...",
    summaryPreviewLabel: "Anteprima del riassunto",
    transcriptPreviewLabel: "Anteprima della trascrizione",
    thumbnailAlt: "Miniatura del Reel Instagram",
    viewsLabel: "visualizzazioni",
    likesLabel: "mi piace",
    lockedHint: "Continua per sbloccare l'output completo.",
    lockedDescription: "Ottieni il risultato completo ed esporta in più formati.",
    cta: "Ottieni trascrizione completa",
    errorInvalidUrl: "Inserisci un link valido di Reel Instagram.",
    errorMissingApi: "Configurazione dell'endpoint API mancante. Riprova più tardi.",
    errorRateLimit: "Limite raggiunto. Registrati per continuare a usare la funzione Reel di Instagram.",
    errorNetwork: "Non siamo riusciti a raggiungere Instagram. Riprova.",
    loadingHint: "La maggior parte delle anteprime è pronta in circa 30 secondi.",
    loadingMessages: [
      "Il nostro delfino sta prendendo il video.",
      "Il nostro delfino sta trascrivendo l'audio.",
      "Il nostro delfino sta cucinando l'anteprima.",
      "Il nostro delfino sta rifinendo le ultime righe.",
      "Questo potrebbe spaccare.",
    ],
    downloadCtaTitle: "Sblocca i download",
    downloadCtaDescription: "Registrati gratis per scaricare il video originale e la miniatura.",
    downloadVideoButton: "Scarica video",
    downloadThumbnailButton: "Scarica miniatura",
  },
  pt: {
    title: "Cole o link do Reel do Instagram",
    subtitle: "Vamos buscar os detalhes do Reel e mostrar uma prévia.",
    analyzeButton: "Analisar Reel",
    analyzingButton: "Analisando...",
    analyzeButtonTikTok: "Analisar TikTok",
    analyzingButtonTikTok: "Analisando TikTok...",
    rateLimitButton: "Cadastre-se para continuar",
    placeholder: "https://www.instagram.com/reel/...",
    summaryPreviewLabel: "Prévia do resumo",
    transcriptPreviewLabel: "Prévia da transcrição",
    thumbnailAlt: "Miniatura do Reel do Instagram",
    viewsLabel: "visualizações",
    likesLabel: "curtidas",
    lockedHint: "Continue para desbloquear o resultado completo.",
    lockedDescription: "Obtenha o resultado completo e exporte em varios formatos.",
    cta: "Obter transcrição completa",
    errorInvalidUrl: "Insira um link valido de Reel do Instagram.",
    errorMissingApi: "Configuração do endpoint de API ausente. Tente novamente mais tarde.",
    errorRateLimit: "Limite excedido. Cadastre-se para continuar usando o recurso de Reels do Instagram.",
    errorNetwork: "Não foi possível acessar o Instagram. Tente novamente.",
    loadingHint: "A maioria das prévias fica pronta em cerca de 30 segundos.",
    loadingMessages: [
      "Nosso golfinho está pegando o vídeo.",
      "Nosso golfinho está transcrevendo o áudio.",
      "Nosso golfinho está preparando a prévia.",
      "Nosso golfinho está finalizando as últimas linhas.",
      "Esse aqui pode ser um sucesso.",
    ],
    downloadCtaTitle: "Desbloqueie os downloads",
    downloadCtaDescription: "Cadastre-se grátis para baixar o vídeo original e a miniatura.",
    downloadVideoButton: "Baixar vídeo",
    downloadThumbnailButton: "Baixar miniatura",
  },
};

const formatCount = (count?: number) => {
  if (!count) return "—";
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
  if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
  return count.toString();
};

const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}:${remaining.toString().padStart(2, "0")}`;
};

const replaceInstagramTerms = (value: string) =>
  value
    .replace(/Instagram Reels?/gi, match => (match.toLowerCase().endsWith("s") ? "TikTok videos" : "TikTok video"))
    .replace(/Reels/gi, "videos")
    .replace(/Reel/gi, "video")
    .replace(/Instagram/gi, "TikTok");

export default function InstagramReelIntake({
  mode: _mode,
  variant = "section",
  previewEndpoint,
  platform = "instagram",
  copyOverride,
  onPreviewLoaded,
  externalPreview = null,
  hideInput = false,
  thumbnailScale,
}: InstagramReelIntakeProps) {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [response, setResponse] = useState<InstagramPreviewResponse | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const requestStartedAt = useRef<number | null>(null);
  const router = useRouter();
  const pathname = usePathname();
  const apiUrl = process.env.NEXT_PUBLIC_API_ENDPOINT as string;

  const locale = useMemo(() => (pathname ? getLocaleFromPathname(pathname) : DEFAULT_LOCALE), [pathname]);
  const copy = useMemo(() => localeCopy[locale] ?? localeCopy[DEFAULT_LOCALE], [locale]);
  const resolvedCopy = useMemo(() => {
    const baseCopy =
      platform === "instagram"
        ? copy
        : {
            ...copy,
            title: replaceInstagramTerms(copy.title),
            subtitle: replaceInstagramTerms(copy.subtitle),
            analyzeButton: copy.analyzeButtonTikTok,
            analyzingButton: copy.analyzingButtonTikTok,
            placeholder: "https://www.tiktok.com/@username/video/...",
            thumbnailAlt: replaceInstagramTerms(copy.thumbnailAlt),
            errorInvalidUrl: replaceInstagramTerms(copy.errorInvalidUrl),
            errorNetwork: replaceInstagramTerms(copy.errorNetwork),
          };

    if (locale !== DEFAULT_LOCALE || !copyOverride) {
      return baseCopy;
    }

    return {
      ...baseCopy,
      ...copyOverride,
    };
  }, [copy, copyOverride, locale, platform]);
  const signupPath = useMemo(() => withSeoLocalePath(locale, PAGES.SIGN_UP.PATH), [locale]);
  const isHero = variant === "hero";
  const mode = _mode;
  const effectiveThumbnailScale = thumbnailScale ?? (isHero ? 0.5 : 1);
  const effectiveResponse = externalPreview ?? (response && response.status === "ok" ? response : null);
  const hasSuccessPreview = Boolean(effectiveResponse);
  const signupPathWithTrialClaim = useMemo(
    () => buildPathWithTrialLinkClaim(signupPath, effectiveResponse?.trial_claim_token ?? null),
    [effectiveResponse?.trial_claim_token, signupPath]
  );

  useEffect(() => {
    if (status !== "loading") {
      setAnalysisProgress(0);
      return;
    }

    setAnalysisProgress(0);
    const interval = window.setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 95) return prev;
        const bump = Math.floor(Math.random() * 4) + 1;
        return Math.min(prev + bump, 95);
      });
    }, 160);

    return () => window.clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (status !== "loading") {
      setLoadingMessageIndex(0);
      return;
    }

    setLoadingMessageIndex(0);
    const interval = window.setInterval(() => {
      setLoadingMessageIndex(prev => Math.min(prev + 1, resolvedCopy.loadingMessages.length - 1));
    }, 3200);

    return () => window.clearInterval(interval);
  }, [resolvedCopy.loadingMessages, status]);

  const currentLoadingMessage =
    resolvedCopy.loadingMessages[Math.min(loadingMessageIndex, resolvedCopy.loadingMessages.length - 1)] ?? resolvedCopy.analyzingButton;

  const handleUpgradeClick = (location: string, feature: string) => {
    trackPremiumFeatureClicked(feature);
    trackAnalyticsApiEvent("instagram_conversion_click", { location, source: location, platform, mode });
    router.push(signupPathWithTrialClaim);
  };

  const handleSubmit = async () => {
    if (isRateLimited) {
      handleUpgradeClick("rate_limit", "unlock_full_transcript");
      return;
    }

    const normalizedUrl = normalizeExternalUrl(url);
    if (!normalizedUrl || !isValidSocialUrl(normalizedUrl, platform)) {
      setStatus("error");
      setIsRateLimited(false);
      setResponse({ status: "error", message: resolvedCopy.errorInvalidUrl });
      onPreviewLoaded?.(null);
      return;
    }

    setStatus("loading");
    setResponse(null);
    setIsRateLimited(false);
    onPreviewLoaded?.(null);
    requestStartedAt.current = performance.now();

    if (!apiUrl) {
      setStatus("error");
      setResponse({ status: "error", message: resolvedCopy.errorMissingApi });
      return;
    }

    const source = getSourceFromUrl(normalizedUrl);
    trackLinkPasted(source, { surface: "public_preview" });
    trackAnalyticsApiEvent("instagram_preview_requested", { source: "link", platform });
    const defaultPreviewEndpoint = mode === "recipe" ? "/public/tools/instagram/reel/recipe" : "/public/tools/instagram/preview";
    const candidates = buildEndpointCandidates(previewEndpoint, defaultPreviewEndpoint).map(e => `${apiUrl}${e}`);

    try {
      const result = await fetchWithFallback<InstagramPreviewResponse>(candidates, { url: normalizedUrl });

      if ("rateLimited" in result) {
        setStatus("error");
        setIsRateLimited(true);
        setPaywallFeature("unlock_full_transcript");
        setResponse({ status: "error", message: resolvedCopy.errorRateLimit });
        trackAnalyticsApiEvent("instagram_preview_failed", { platform, reason: "rate_limited", error: "rate_limited" });
        return;
      }

      const { res, data } = result;
      if (res.ok && data.status === "ok") {
        setResponse(data);
        onPreviewLoaded?.(data);
        setStatus("success");
        const elapsedMs = requestStartedAt.current ? Math.round(performance.now() - requestStartedAt.current) : undefined;
        trackAnalyticsApiEvent("instagram_preview_loaded", {
          platform,
          duration_sec: data.video.duration_sec,
          elapsed_ms: elapsedMs,
        });
        return;
      }

      setStatus("error");
      onPreviewLoaded?.(null);
      setResponse(data ?? { status: "error", message: resolvedCopy.errorNetwork });
      const reason = classifyPreviewFailureReason({
        status: res.status,
        message: data.status === "error" ? data.message : null,
        reason: data.status === "error" ? data.reason : null,
      });
      trackAnalyticsApiEvent("instagram_preview_failed", { platform, reason, error: "api_error" });
    } catch {
      setStatus("error");
      onPreviewLoaded?.(null);
      setResponse({ status: "error", message: resolvedCopy.errorNetwork });
      trackAnalyticsApiEvent("instagram_preview_failed", { platform, reason: "timeout", error: "network_error" });
    }
  };

  const previewThumbnailSrc = effectiveResponse
    ? (getProxiedImageSrc(effectiveResponse.video.thumbnail_url) ??
      effectiveResponse.video.thumbnail_url ??
      "/preview/hormozi-thumbnail.webp")
    : "/preview/hormozi-thumbnail.webp";

  return (
    <>
      {paywallFeature && (
        <PaywallTracker trigger="premium_feature_clicked" feature={paywallFeature} onTracked={() => setPaywallFeature(null)} />
      )}
      <section className={isHero ? "w-full max-w-xl" : "w-full max-w-4xl mx-auto px-4 py-10"}>
        <div
          className={["border border-gray-200 rounded-3xl", isHero ? "bg-white/95 shadow-md p-5" : "bg-white shadow-lg p-6 md:p-10"].join(
            " "
          )}
        >
          {!hideInput && (
            <div className="flex flex-col gap-4">
              <div>
                <h2 className={isHero ? "text-xl font-semibold" : "text-2xl font-semibold"}>{resolvedCopy.title}</h2>
                <p className="text-gray-600 mt-2">{resolvedCopy.subtitle}</p>
              </div>
              <div className="flex flex-col md:flex-row gap-3 items-stretch">
                <Input
                  value={url}
                  onChange={event => setUrl(event.target.value)}
                  onPaste={event => {
                    if (event.clipboardData?.getData("text")) {
                      trackAnalyticsApiEvent("instagram_link_pasted", { platform });
                    }
                  }}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      void handleSubmit();
                    }
                  }}
                  placeholder={resolvedCopy.placeholder}
                  aria-label={platform === "instagram" ? "Instagram Reel URL" : "TikTok video URL"}
                  aria-invalid={status === "error"}
                  className="h-11"
                />
                <Button onClick={handleSubmit} isLoading={status === "loading"} className="h-11 px-6">
                  {status === "loading"
                    ? resolvedCopy.analyzingButton
                    : isRateLimited
                      ? resolvedCopy.rateLimitButton
                      : resolvedCopy.analyzeButton}
                </Button>
              </div>
              {status === "loading" && (
                <div className="rounded-2xl border border-sky-200 bg-gradient-to-r from-sky-50 via-cyan-50 to-emerald-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm text-sky-950" aria-live="polite">
                      <span className="h-2.5 w-2.5 rounded-full bg-sky-500 animate-pulse" />
                      <span>{currentLoadingMessage}</span>
                    </div>
                    <span className="text-sm font-semibold text-sky-900">{analysisProgress}%</span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/80">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-sky-500 to-emerald-400 transition-[width] duration-500 ease-out"
                      style={{ width: `${analysisProgress}%` }}
                    />
                  </div>
                  <p className="mt-3 text-xs text-sky-900/80">{resolvedCopy.loadingHint}</p>
                </div>
              )}
              {effectiveResponse?.media?.cache_state === "expired" && (
                <p className="text-sm text-muted-foreground" role="status">
                  {mediaRetentionCopy[locale].explanation}
                </p>
              )}
              {status === "error" && response?.status === "error" && <p className="text-sm text-destructive">{response.message}</p>}
            </div>
          )}

          {hasSuccessPreview && effectiveResponse && (
            <div className={isHero ? "mt-6 grid gap-5" : "mt-8 grid gap-6 md:grid-cols-[180px_1fr] items-start"}>
              <div
                className="rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 mx-auto"
                style={isHero && effectiveThumbnailScale < 1 ? { maxWidth: `${Math.round(effectiveThumbnailScale * 100)}%` } : undefined}
              >
                <Image
                  src={previewThumbnailSrc}
                  alt={resolvedCopy.thumbnailAlt}
                  width={360}
                  height={640}
                  sizes="(max-width: 768px) 100vw, 180px"
                  className="w-full h-auto"
                  loading={isHero ? "eager" : "lazy"}
                  unoptimized
                />
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <div className="flex flex-wrap gap-2 items-center text-sm text-gray-600">
                    <span className="font-medium text-gray-900">@{effectiveResponse.video.author.username}</span>
                    <span>•</span>
                    <span>{effectiveResponse.video.author.display_name}</span>
                    <span>•</span>
                    <span>{formatDuration(effectiveResponse.video.duration_sec)}</span>
                  </div>
                  {effectiveResponse.video.caption && (
                    <p className="mt-2 text-gray-800 whitespace-pre-wrap">{effectiveResponse.video.caption}</p>
                  )}
                  <div className="flex gap-4 text-sm text-gray-600 mt-3">
                    <span>
                      {formatCount(effectiveResponse.video.view_count)} {resolvedCopy.viewsLabel}
                    </span>
                    <span>
                      {formatCount(effectiveResponse.video.like_count)} {resolvedCopy.likesLabel}
                    </span>
                  </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 space-y-4">
                  {mode === "recipe" && effectiveResponse.recipe && (
                    <div>
                      {effectiveResponse.recipe.title && (
                        <p className="text-base font-semibold text-gray-900">{effectiveResponse.recipe.title}</p>
                      )}
                      {effectiveResponse.recipe.description && <p className="mt-1 text-gray-700">{effectiveResponse.recipe.description}</p>}

                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                        {effectiveResponse.recipe.servings && <span>Servings: {effectiveResponse.recipe.servings}</span>}
                        {typeof effectiveResponse.recipe.prep_time_minutes === "number" && (
                          <span>Prep: {effectiveResponse.recipe.prep_time_minutes} min</span>
                        )}
                        {typeof effectiveResponse.recipe.cook_time_minutes === "number" && (
                          <span>Cook: {effectiveResponse.recipe.cook_time_minutes} min</span>
                        )}
                        {typeof effectiveResponse.recipe.total_time_minutes === "number" && (
                          <span>Total: {effectiveResponse.recipe.total_time_minutes} min</span>
                        )}
                      </div>

                      {!!effectiveResponse.recipe.ingredients?.length && (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-widest text-gray-500">Ingredients</p>
                          <ul className="mt-2 list-disc pl-5 text-gray-800 space-y-1">
                            {effectiveResponse.recipe.ingredients.slice(0, 6).map((ingredient, index) => (
                              <li key={`ingredient-${index}`}>{ingredient}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {!!effectiveResponse.recipe.steps?.length && (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-widest text-gray-500">Steps</p>
                          <ol className="mt-2 list-decimal pl-5 text-gray-800 space-y-1">
                            {effectiveResponse.recipe.steps.slice(0, 4).map((step, index) => (
                              <li key={`step-${index}`}>{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}

                      {!!effectiveResponse.recipe.notes?.length && (
                        <div className="mt-3">
                          <p className="text-xs uppercase tracking-widest text-gray-500">Notes</p>
                          <ul className="mt-2 list-disc pl-5 text-gray-700 space-y-1">
                            {effectiveResponse.recipe.notes.slice(0, 2).map((note, index) => (
                              <li key={`note-${index}`}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {effectiveResponse.hook?.hook && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">Hook preview</p>
                      <p className="mt-2 text-gray-800 whitespace-pre-wrap">{effectiveResponse.hook.hook}</p>
                    </div>
                  )}
                  {(effectiveResponse.blog?.title ||
                    effectiveResponse.blog?.intro ||
                    !!effectiveResponse.blog?.sections?.length ||
                    effectiveResponse.blog?.conclusion) && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">Blog preview</p>
                      {effectiveResponse.blog?.title && (
                        <p className="mt-2 text-base font-semibold text-gray-900">{effectiveResponse.blog.title}</p>
                      )}
                      {effectiveResponse.blog?.intro && (
                        <p className="mt-2 text-gray-800 whitespace-pre-wrap">{effectiveResponse.blog.intro}</p>
                      )}
                      {!!effectiveResponse.blog?.sections?.length && (
                        <div className="mt-3 space-y-2">
                          {effectiveResponse.blog.sections.slice(0, 3).map((section, index) => {
                            if (typeof section === "string") {
                              return (
                                <p key={`blog-section-${index}`} className="text-gray-700 whitespace-pre-wrap">
                                  {section}
                                </p>
                              );
                            }

                            return (
                              <div key={`blog-section-${index}`}>
                                {section.heading && <p className="text-sm font-semibold text-gray-900">{section.heading}</p>}
                                {section.content && <p className="text-gray-700 whitespace-pre-wrap">{section.content}</p>}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {effectiveResponse.blog?.conclusion && (
                        <p className="mt-3 text-gray-700 whitespace-pre-wrap">{effectiveResponse.blog.conclusion}</p>
                      )}
                    </div>
                  )}
                  {effectiveResponse.preview.teaser_summary && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.summaryPreviewLabel}</p>
                      <p className="mt-2 text-gray-800 whitespace-pre-wrap">{effectiveResponse.preview.teaser_summary}</p>
                    </div>
                  )}
                  {effectiveResponse.preview.teaser_transcript && (
                    <div>
                      <p className="text-xs uppercase tracking-widest text-gray-500">{copy.transcriptPreviewLabel}</p>
                      <p className="mt-2 text-gray-800 whitespace-pre-wrap">{effectiveResponse.preview.teaser_transcript}</p>
                    </div>
                  )}
                  <div className="rounded-2xl border border-primary-200 bg-primary-50/60 p-4">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                          <PiLockBold className="h-4 w-4 text-primary-400" />
                          <span>{copy.downloadCtaTitle}</span>
                        </div>
                        <p className="mt-2 text-sm text-gray-700">{copy.downloadCtaDescription}</p>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row">
                        <Button
                          variant="outline"
                          className="justify-center"
                          onClick={() => handleUpgradeClick("preview_download_video", "download_preview_video")}
                        >
                          <PiVideo className="h-4 w-4" />
                          {copy.downloadVideoButton}
                        </Button>
                        <Button
                          variant="outline"
                          className="justify-center"
                          onClick={() => handleUpgradeClick("preview_download_thumbnail", "download_preview_thumbnail")}
                        >
                          <PiDownloadSimple className="h-4 w-4" />
                          {copy.downloadThumbnailButton}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-400">{copy.lockedHint}</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <div className="text-sm text-gray-600">{copy.lockedDescription}</div>
                  <Button className="px-6" onClick={() => handleUpgradeClick("preview_cta", "unlock_full_transcript")}>
                    {copy.cta}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
